import type Konva from "konva";

import { rotatePoint } from "~/telestrations/helpers/spotlightShapeConfig";

interface IPoint {
  x: number;
  y: number;
}

// Shape transformation properties
interface ITransformProps {
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
}

// Extracts transformation properties from a Konva shape
export function getShapeTransform(shape: Konva.Shape): ITransformProps {
  return {
    x: shape.x(),
    y: shape.y(),
    rotation: shape.rotation(),
    scaleX: shape.scaleX(),
    scaleY: shape.scaleY(),
  };
}

// Applies scaling to coordinates
export function applyScale(props: ITransformProps): IPoint {
  return {
    x: props.x * props.scaleX,
    y: props.y * props.scaleY,
  };
}

// Applies inverse scaling to coordinates
export function applyInverseScale(props: ITransformProps): IPoint {
  return {
    x: props.x / props.scaleX,
    y: props.y / props.scaleY,
  };
}

/**
 * Applies rotation to a point
 * This handles the rotation of a point around the origin
 */
export function applyRotation(point: IPoint, rotation: number): IPoint {
  return rotation === 0 ? point : rotatePoint(point, rotation);
}

export function applyInverseRotation(point: IPoint, rotation: number): IPoint {
  return rotation === 0 ? point : rotatePoint(point, -rotation);
}

/**
 * Calculates scaled and rotated coordinates for converting Konva shapes to SVG
 * This handles the transformation from Konva's transformed coordinates to SVG readable coordinates
 */
export function getScaledAndRotatedCoordinates(shape: Konva.Shape): IPoint {
  const transform = getShapeTransform(shape);
  const scaledPoint = applyInverseScale(transform);

  return applyInverseRotation(scaledPoint, transform.rotation);
}

/**
 * Converts SVG readable coordinates back to Konva transformed coordinates
 * This handles the inverse transformation from SVG to Konva's coordinate system
 */
export function getKonvaCoordinatesFromSVG(props: ITransformProps): IPoint {
  const rotatedPoint = applyRotation({ x: props.x, y: props.y }, props.rotation);

  return applyScale({
    x: rotatedPoint.x,
    y: rotatedPoint.y,
    rotation: props.rotation,
    scaleX: props.scaleX,
    scaleY: props.scaleY,
  });
}
